import React, { useState, useRef, useEffect } from 'react';
import { EdgeProps, getBezierPath, EdgeLabelRenderer } from 'reactflow';
import { EdgeData } from '../types';

interface CustomEdgeProps extends EdgeProps {
  data?: EdgeData;
  updateEdge?: (id: string, updates: any) => void;
}

const CustomEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  label,
  labelStyle,
  labelBgStyle,
  labelBgPadding,
  labelBgBorderRadius,
  data,
  updateEdge,
  animated,
}: CustomEdgeProps) => {
  // Get the display label
  const displayLabel = data?.labelVisible !== false 
    ? (data?.customLabel || data?.type || label || '')
    : '';

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(displayLabel);
  const [isDrawing, setIsDrawing] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasAnimatedRef = useRef(false);

  // Update editValue when displayLabel changes (but not while editing)
  useEffect(() => {
    if (!isEditing) {
      setEditValue(displayLabel);
    }
  }, [displayLabel, isEditing]);

  // Calculate edge path
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  });

  // Check if edge is animated (default to true if not explicitly set to false)
  const isAnimated = animated !== false;

  // Handle draw-in animation completion - only run once on mount
  useEffect(() => {
    if (!hasAnimatedRef.current && isDrawing) {
      hasAnimatedRef.current = true;
      const drawDuration = 600; // Match CSS animation duration
      const timer = setTimeout(() => {
        setIsDrawing(false);
      }, drawDuration);
      return () => clearTimeout(timer);
    }
  }, []);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Handle double click to start editing
  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
    setEditValue(displayLabel);
  };

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditValue(e.target.value);
  };

  // Handle save on blur or Enter
  const handleSave = () => {
    if (updateEdge) {
      updateEdge(id, {
        data: {
          ...data,
          customLabel: editValue.trim() || undefined,
        },
      });
    }
    setIsEditing(false);
  };

  // Handle key down
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditValue(displayLabel);
    }
  };

  // For animated edges, we'll use CSS-based opacity animation
  // The gradient effect will be achieved through CSS animations
  // Keep the original stroke color for the base
  const strokeStyle = {
    ...style,
    // Add a subtle opacity animation for flowing effect on animated edges
    ...(isAnimated ? {
      opacity: style.opacity !== undefined ? style.opacity : 0.9,
    } : {})
  };

  // Don't render label if not visible
  if (data?.labelVisible === false || !displayLabel) {
    return (
      <path
        id={id}
        style={strokeStyle}
        className="react-flow__edge-path"
        data-drawing={isDrawing ? 'true' : undefined}
        d={edgePath}
        markerEnd={markerEnd}
      />
    );
  }

  return (
    <>
      <path
        id={id}
        style={strokeStyle}
        className="react-flow__edge-path"
        data-drawing={isDrawing ? 'true' : undefined}
        d={edgePath}
        markerEnd={markerEnd}
      />
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: 'all',
          }}
          className="nodrag nopan"
        >
          {isEditing ? (
            <input
              ref={inputRef}
              type="text"
              value={editValue}
              onChange={handleChange}
              onBlur={handleSave}
              onKeyDown={handleKeyDown}
              style={{
                background: labelBgStyle?.fill || '#ffffff',
                border: `1px solid ${labelStyle?.fill || '#000000'}`,
                borderRadius: `${labelBgBorderRadius || 4}px`,
                padding: `${labelBgPadding?.[1] || 4}px ${labelBgPadding?.[0] || 8}px`,
                fontSize: labelStyle?.fontSize || '10px',
                fontWeight: labelStyle?.fontWeight || 600,
                color: labelStyle?.fill || '#000000',
                outline: 'none',
                minWidth: '60px',
                textAlign: 'center',
              }}
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
            />
          ) : (
            <div
              onDoubleClick={handleDoubleClick}
              className="animate-fade-in transition-all duration-200 hover:scale-105"
              style={{
                background: labelBgStyle?.fill || '#ffffff',
                border: `1px solid ${labelBgStyle?.stroke || labelStyle?.fill || '#000000'}`,
                borderRadius: `${labelBgBorderRadius || 4}px`,
                padding: `${labelBgPadding?.[1] || 4}px ${labelBgPadding?.[0] || 8}px`,
                fontSize: labelStyle?.fontSize || '10px',
                fontWeight: labelStyle?.fontWeight || 600,
                color: labelStyle?.fill || '#000000',
                cursor: 'text',
                userSelect: 'none',
                whiteSpace: 'nowrap',
                willChange: 'transform',
              }}
              title="Double-click to edit"
            >
              {displayLabel}
            </div>
          )}
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

export default CustomEdge;
